import moment from 'moment'
import * as React from 'react'
import styled from 'styled-components'
import {
  FavoriteBorderRounded,
  FavoriteRounded,
  ExpandMoreRounded,
  ExpandLessRounded,
} from '@material-ui/icons'
import { createMuiTheme, IconButton, ThemeProvider, useMediaQuery } from '@material-ui/core'
import NewComment from './NewComment'
import { EnrichedCommentData } from '../details/CommentsDetails'
import { rwStreamClientAtom } from '../../../atoms/StreamClient'
import { VideoData } from '../../../interfaces'
import {
  CreateReply,
  DeleteReply,
  GetCommentReplies,
  LikeReply,
  UnlikeReply,
} from '../../../data/Reactions'
import { useAtom } from 'jotai'
import { Trash2 } from '@geist-ui/icons'
import { rwDeleteDialogAtom } from '../../../atoms/Delete'
import { useFirebaseAuthGuard } from '../../../hooks/firebase'
import { useInfiniteLoadComment } from '../../../hooks/inifiniteLoadComment'
import { LoadingType } from '../../../hooks/infiniteLoadByCursor'
import { animated, useSpring } from 'react-spring'
import { fbEnrichUsersFromIds } from '../../../data/FirebaseEnrich'
import ProfileImage from '../../profile/image'
import { useRouter } from 'next/router'
import useAuth from '../../../hooks/auth'

const OuterDiv = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: flex-start;
`

const InnerDiv = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: flex-start;
`

const CommentDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  margin-left: 15px;
`

const TitleDiv = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: flex-start;
`

const Name = styled(animated.div)`
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;

  @media (min-width: 43em) {
    font-size: 17px;
  }
`

const DateDiv = styled.div`
  color: #cdcdcd;
  font-size: 14px;
  font-weight: 500;
  margin-left: 5px;

  @media (min-width: 43em) {
    font-size: 17px;
  }
`

const Content = styled.div`
  color: white;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 5px;
  white-space: pre-wrap;
  width: 100%;

  @media (min-width: 43em) {
    font-size: 17px;
  }
`

const ActionsDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const LeftActionsDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const RightActionsDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
`

const ActionDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
  margin-right: 15px;
`

const Action = styled(animated.div)`
  color: white;
  font-size: 14px;
  font-weight: 600;

  @media (min-width: 43em) {
    font-size: 17px;
  }
`

const ViewRepliesDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
  margin-top: 5px;
`

const MoreRepliesDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
`

const Divider = styled.div`
  background: #323232;
  height: 1px;
  width: 100%;
  margin: 15px 0;
`

const theme = createMuiTheme({
  palette: {
    type: 'dark',
  },
})

interface CommentProps {
  video: VideoData
  comment: EnrichedCommentData
  hideReply: boolean
  like: (comment_id: string, reply_id?: string) => void
  unlike: (comment_id: string, like_id: string, reply_id?: string) => void
  deleteComment: (comment_id: string, reply_id?: string) => void
  updateHeight: () => void
}

const Comment = ({
  video,
  comment,
  like,
  unlike,
  hideReply,
  deleteComment,
  updateHeight,
}: CommentProps): JSX.Element => {
  const isMobile = useMediaQuery('(max-width: 43em)')
  const [showReply, setShowReply] = React.useState(false)
  const [showReplies, setShowReplies] = React.useState(false)
  const [streamClient] = useAtom(rwStreamClientAtom)
  const { profile } = useAuth()
  const [, setDelete] = useAtom(rwDeleteDialogAtom)
  const [profileHover, setProfileHover] = React.useState(false)
  const [replyHover, setReplyHover] = React.useState(false)
  const router = useRouter()

  const profileHoverState = useSpring({
    cursor: 'pointer',
    borderRadius: isMobile ? 40 : 50,
    minWidth: isMobile ? '40px' : '50px',
    minHeight: isMobile ? '40px' : '50px',
    transform: profileHover ? `translate3d(0,-5%,0)` : `translate3d(0,0%,0)`,
    config: {
      mass: 1,
      tension: 170,
      friction: 26,
      velocity: 0,
    },
  })

  const textHoverState = useSpring({
    cursor: 'pointer',
    textDecoration: profileHover ? 'underline' : 'none',
    config: {
      mass: 1,
      tension: 170,
      friction: 26,
      velocity: 0,
    },
  })

  const replyHoverState = useSpring({
    cursor: 'pointer',
    color: replyHover ? 'white' : '#cdcdcd',
    config: {
      mass: 1,
      tension: 170,
      friction: 26,
      velocity: 0,
    },
  })

  const getRepliesForComment = React.useCallback(
    async (
      lastID: string
    ): Promise<{ items: EnrichedCommentData[]; count: number; nextID: string }> => {
      const comments = await streamClient.reactions.filter({
        activity_id: video.activity_id,
        id_gte: comment.id,
        limit: 1,
        kind: 'comment',
      })

      const count =
        comments.results.length > 0 && (comments.results[0] as any).children_counts
          ? ((comments.results[0] as any).children_counts.reply as number)
          : 0

      const resp = await GetCommentReplies(profile?.address ?? '', comment.id, lastID)
      const users = await fbEnrichUsersFromIds((resp.replies || []).map((reply) => reply.user_id))

      const replyData = resp.replies
        ? resp.replies.map((reply) => {
            return {
              user: users.find((user) => user.address === reply.user_id),
              id: reply.id,
              created_at: new Date(reply.created_at * 1000),
              content: reply.content,
              replies_count: reply.replies_count,
              likes_count: reply.likes_count,
              liked: reply.liked_reaction_id === '' ? undefined : reply.liked_reaction_id,
              own: profile?.address === reply.user_id,
              parent_id: comment.id,
            }
          })
        : []

      return {
        items: replyData as EnrichedCommentData[],
        count: count,
        nextID: replyData.length > 0 ? replyData[replyData.length - 1].id : '',
      }
    },
    [comment.id, profile?.address, streamClient.reactions, video.activity_id]
  )

  const {
    items,
    count,
    hasNext,
    loadNext,
    loadMoreRef,
    newItem,
    likeItem,
    unlikeItem,
    deleteItem,
  } = useInfiniteLoadComment<EnrichedCommentData>({
    id: video.address,
    getItems: getRepliesForComment,
    loadingType: LoadingType.MANUAL,
  })

  const submitReply = React.useCallback(
    async (comment: string, comment_id?: string): Promise<void> => {
      if (comment_id) {
        const resp = await CreateReply(video.created_by, video.activity_id, comment_id, comment)
        if (!resp) {
          return
        }

        const users = await fbEnrichUsersFromIds([resp.reply.user_id])

        newItem({
          user: users[0],
          id: resp.reply.id,
          created_at: new Date(resp.reply.created_at * 1000),
          content: resp.reply.content,
          replies_count: resp.reply.replies_count,
          liked: resp.reply.liked_reaction_id === '' ? undefined : resp.reply.liked_reaction_id,
          own: profile?.address === resp.reply.user_id,
          likes_count: resp.reply.likes_count,
        })
        setShowReply(false)
        setShowReplies(true)
      } else {
        return
      }
    },
    [newItem, profile?.address, video.activity_id, video.created_by]
  )

  const likeReply = async (comment_id: string, reply_id?: string): Promise<void> => {
    if (!reply_id) {
      return
    }

    const like = await LikeReply(video.created_by, video.activity_id, comment_id, reply_id)
    likeItem(reply_id, like.liked_reaction_id, like.likes)
  }

  const unlikeReply = async (
    comment_id: string,
    like_id: string,
    reply_id?: string
  ): Promise<void> => {
    if (!reply_id) {
      return
    }

    await UnlikeReply(video.created_by, video.activity_id, comment_id, reply_id, like_id)
    unlikeItem(reply_id)
  }

  const deleteReply = async (comment_id: string, reply_id?: string): Promise<void> => {
    if (!reply_id) {
      return
    }
    await DeleteReply(video.created_by, video.activity_id, comment_id, reply_id)
    deleteItem(reply_id)
  }

  const handleAction = React.useCallback(() => {
    if (!comment.liked) {
      like(
        comment.parent_id ? comment.parent_id : comment.id,
        comment.parent_id ? comment.id : comment.parent_id
      )
    } else {
      unlike(
        comment.parent_id ? comment.parent_id : comment.id,
        comment.liked,
        comment.parent_id ? comment.id : comment.parent_id
      )
    }
  }, [comment.id, comment.liked, comment.parent_id, like, unlike])
  const guardedAction = useFirebaseAuthGuard(handleAction)

  const handleDelete = React.useCallback(
    () =>
      setDelete({
        comment_id: comment.parent_id ? comment.parent_id : comment.id,
        reply_id: comment.parent_id ? comment.id : comment.parent_id,
        delete: deleteComment,
      }),
    [comment.id, comment.parent_id, deleteComment, setDelete]
  )
  const guardedDelete = useFirebaseAuthGuard(handleDelete)

  const openProfile = (address: string): void => {
    router.push(`/${address}`)
  }

  React.useEffect(() => {
    updateHeight()
  }, [showReply, showReplies])

  return (
    <OuterDiv>
      <Divider />
      <InnerDiv>
        {comment.user && (
          <animated.div
            onMouseEnter={() => setProfileHover(true)}
            onMouseLeave={() => setProfileHover(false)}
            style={profileHoverState}
            onClick={() => openProfile(comment.user.address)}
          >
            <ProfileImage item={comment.user} size={isMobile ? 40 : 50} />
          </animated.div>
        )}
        <CommentDiv>
          <TitleDiv>
            <Name
              onMouseEnter={() => setProfileHover(true)}
              onMouseLeave={() => setProfileHover(false)}
              style={textHoverState}
              onClick={() => openProfile(comment.user.address)}
            >
              {comment.user.displayName}
            </Name>
            <DateDiv>{' · ' + moment(comment.created_at).fromNow()}</DateDiv>
          </TitleDiv>
          <Content>{comment.content}</Content>
          <ActionsDiv>
            <LeftActionsDiv>
              <ActionDiv onClick={guardedAction}>
                <ThemeProvider theme={theme}>
                  <IconButton size="small" disableFocusRipple>
                    {!comment.liked ? (
                      <FavoriteBorderRounded
                        style={{
                          width: 18,
                          height: 18,
                          marginLeft: `-3px`,
                          marginRight: `3px`,
                        }}
                      />
                    ) : (
                      <FavoriteRounded
                        style={{
                          width: 18,
                          height: 18,
                          marginLeft: `-3px`,
                          marginRight: `3px`,
                        }}
                      />
                    )}
                  </IconButton>
                </ThemeProvider>
                <Action>{comment.likes_count}</Action>
              </ActionDiv>
              {!hideReply && (
                <ActionDiv onClick={() => setShowReply(!showReply)}>
                  <Action
                    onMouseEnter={() => setReplyHover(true)}
                    onMouseLeave={() => setReplyHover(false)}
                    style={replyHoverState}
                  >
                    REPLY
                  </Action>
                </ActionDiv>
              )}
              {comment.own && (
                <RightActionsDiv onClick={guardedDelete}>
                  <Trash2 size={18} color={'#ff453a'} style={{ cursor: 'pointer' }} />
                </RightActionsDiv>
              )}
            </LeftActionsDiv>
          </ActionsDiv>
          {showReply && (
            <NewComment
              video={video}
              id={comment.id}
              submit={submitReply}
              cancel={() => setShowReply(false)}
              typing={updateHeight}
            />
          )}
          {(count !== undefined ? count > 0 : comment.replies_count > 0) && (
            <ViewRepliesDiv
              onClick={() => {
                if (!showReplies) {
                  loadNext()
                }
                setShowReplies(!showReplies)
              }}
            >
              <ThemeProvider theme={theme}>
                {showReplies ? (
                  <ExpandLessRounded
                    htmlColor="#D5D4FB"
                    style={{
                      width: 32,
                      height: 32,
                      marginLeft: `-5px`,
                    }}
                  />
                ) : (
                  <ExpandMoreRounded
                    htmlColor="#D5D4FB"
                    style={{
                      width: 32,
                      height: 32,
                      marginLeft: `-5px`,
                    }}
                  />
                )}
              </ThemeProvider>
              <Action style={{ color: '#D5D4FB' }}>{`${showReplies ? 'Hide' : 'View'} ${
                count ?? comment.replies_count
              } ${
                count
                  ? count === 1
                    ? 'Reply'
                    : 'Replies'
                  : comment.replies_count == 1
                  ? 'Reply'
                  : 'Replies'
              }`}</Action>
            </ViewRepliesDiv>
          )}
          {showReplies &&
            items
              .sort((a, b) => b.created_at.getTime() - a.created_at.getTime())
              .map((comment: EnrichedCommentData, index) => (
                <Comment
                  updateHeight={updateHeight}
                  video={video}
                  comment={comment}
                  hideReply={true}
                  like={likeReply}
                  unlike={unlikeReply}
                  deleteComment={deleteReply}
                  key={`comment-${index}`}
                />
              ))}
          {showReplies && hasNext && (
            <MoreRepliesDiv ref={loadMoreRef} onClick={() => loadNext()}>
              <ThemeProvider theme={theme}>
                <ExpandMoreRounded
                  htmlColor="#D5D4FB"
                  style={{
                    width: 35,
                    height: 35,
                    marginLeft: `-5px`,
                  }}
                />
              </ThemeProvider>
              <Action style={{ color: '#D5D4FB' }}>{`Show More Replies`}</Action>
            </MoreRepliesDiv>
          )}
        </CommentDiv>
      </InnerDiv>
    </OuterDiv>
  )
}

export default Comment
